import { useState, useEffect } from 'react';
import { getItems, createItem, updateItem, FIG_LISTS } from '../../api/figApi';
import generateACORD126 from './generateACORD126';
import {
  ACORD_TRANSACTION_STATUSES,
  DEFAULT_TRANSACTION_STATUS,
} from './acordTransactionStatus';
import { normalizeToIsoDate } from '../../utils/dateNormalize';
import './FIG.css';

const YN = ['Y', 'N'];
const PREMIUM_BASIS = ['', 'S-Sales', 'P-Payroll', 'A-Area', 'C-Contracts', 'U-Units', 'M-Admissions', 'T-Total'];

function ACORD126({ insured, policy, buildings, additionalInterests, classCodes, getToken, onDirtyChange, onBack }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({});
  // UI-only — controls whether top CARRIER / NAIC / POLICY # print on the PDF.
  // Not persisted to SharePoint; not derived from the policy's lifecycle Status.
  const [transactionStatus, setTransactionStatus] = useState(DEFAULT_TRANSACTION_STATUS);

  function handleStatusChange(status) {
    setTransactionStatus(status);
    if (status === 'Renew') {
      // Restore carrier trio from the original policy prop captured at mount
      setForm(prev => ({
        ...prev,
        carrier: p.Carrier || '',
        naicCode: p.NAICCode || '',
        policyNumber: p.PolicyNumber || '',
      }));
    } else {
      setForm(prev => ({ ...prev, carrier: '', naicCode: '', policyNumber: '' }));
    }
  }

  const p = policy || {};
  const propertyInterests = (additionalInterests || []).filter(ai =>
    ['additional insured', 'loss payee', 'mortgagee'].includes((ai.InterestType || '').toLowerCase())
  );
  const selectedInterest = propertyInterests[0] || {};

  // Load class codes for this insured's GL
  const glClassCodes = (classCodes || []).filter(c =>
    (c.CodeType || '').toUpperCase() === 'GL'
  );

  useEffect(() => {
    setForm({
      // Page 1 — Header (read-only from policy)
      date: new Date().toISOString().split('T')[0],
      carrier: p.Carrier || '',
      naicCode: p.NAICCode || '',
      policyNumber: p.PolicyNumber || '',
      // Forward-looking term: 126 is a renewal submission by default — its
      // Effective Date is the START of the proposed new term, which equals
      // the expiring policy's ExpirationDate. User can override the input.
      effectiveDate: normalizeToIsoDate(p.ExpirationDate),
      // Coverage Type
      coverageForm: p.CoverageForm || 'Occurrence',
      otherCoverage: '',
      // Limits
      eachOccurrence: p.EachOccurrenceLimit || '',
      fireDamage: p.DamageToRentedPremises || '',
      medExpense: p.MedicalExpenseLimit || '',
      personalAdvInjury: p.PersonalAdvInjuryLimit || '',
      generalAggregate: p.GeneralAggregateLimit || '',
      aggregateAppliesPer: p.AggregateAppliesPer || 'Policy',
      productsCompOps: p.ProductsCompOpsAggLimit || '',
      employeeBenefits: p.EmployeeBenefitsLimit || '',
      otherLimitDesc: '',
      otherLimitAmt: '',
      // Deductibles
      pdDeductible: p.PDDeductible || '',
      biDeductible: p.BIDeductible || '',
      otherDedDesc: '',
      otherDedAmt: '',
      deductibleBasis: p.DeductibleBasis || 'Per Occurrence',
      // Premiums
      premOpsPremium: p.PremOpsPremium || '',
      productsPremium: p.ProductsPremium || '',
      otherPremium: '',
      totalPremium: p.Premium || '',
      remarks: '',
      // UM/Med Pay
      umAvailable: 'N',
      medPayAvailable: 'N',
      // Schedule of Hazards (3 rows)
      haz: [0, 1, 2].map(i => {
        const cc = glClassCodes[i] || {};
        return {
          loc: cc.LocationNumber || (i === 0 ? '1' : ''),
          haz: String(i + 1),
          classCode: cc.Code || '',
          classification: cc.Description || '',
          premBasis: cc.PremiumBasis || '',
          exposure: cc.Exposure || '',
          territory: cc.Territory || '',
          poRate: cc.Rate || '',
          prodRate: '',
          poPremium: cc.Premium || '',
          prodPremium: '',
        };
      }),
      // Claims Made
      retroDate: '',
      entryDate: '',
      q_AAH: '', q_AAH_exp: '',
      q_AAI: '', q_AAI_exp: '',
      // Employee Benefits
      ebDeductible: '',
      ebEmployees: '',
      ebCovered: '',
      ebRetroDate: '',
      // Page 2 — Contractors
      contractorsFT: '',
      contractorsPT: '',
      subcontractedPct: '',
      subcontractorsPaid: '',
      workSubcontracted: '',
      cq: [
        { code: 'AAB', answer: '', exp: '' },
        { code: 'AAI', answer: '', exp: '' },
        { code: 'AAC', answer: '', exp: '' },
        { code: 'ABB', answer: '', exp: '' },
        { code: 'AAD', answer: '', exp: '' },
        { code: 'AAE', answer: '', exp: '' },
      ],
      // Products (3 rows)
      products: [0, 1, 2].map(() => ({
        name: '', grossSales: '', units: '', monthsInMarket: '',
        expectedLife: '', intendedUse: '', components: '',
      })),
      // Products questions (10)
      pq: [
        { code: 'AAJ', answer: '', exp: '' },
        { code: 'ABA', answer: '', exp: '' },
        { code: 'ABB', answer: '', exp: '' },
        { code: 'ABC', answer: '', exp: '' },
        { code: 'ABD', answer: '', exp: '' },
        { code: 'ABE', answer: '', exp: '' },
        { code: 'ABF', answer: '', exp: '' },
        { code: 'ABG', answer: '', exp: '' },
        { code: 'ABH', answer: '', exp: '' },
        { code: 'ABI', answer: '', exp: '' },
      ],
      // Page 3 — General Info questions
      gq: [
        { code: 'ABJ', answer: '', exp: '' },
        { code: 'ACA', answer: '', exp: '' },
        { code: 'ACJ', answer: '', exp: '' },
        { code: 'ACB', answer: '', exp: '' },
        { code: 'ACC', answer: '', exp: '' },
        { code: 'AAH_c', answer: '', exp: '' },
        { code: 'ACD', answer: '', exp: '' },
        { code: 'KAG', answer: '', exp: '' },
        { code: 'ACE', answer: '', exp: '' },
        { code: 'KAA', answer: '', exp: '' },
        { code: 'KAH', answer: '', exp: '' },
        { code: 'ACF', answer: '', exp: '' },
        { code: 'KAB', answer: '', exp: '' },
        { code: 'ACG', answer: '', exp: '' },
        { code: 'ACH', answer: '', exp: '' },
      ],
      apartmentCount: '',
      apartmentArea: '',
      otherLodging: '',
      // Additional Interest
      interestType: selectedInterest.InterestType || '',
      interestName: selectedInterest.EntityName || '',
      interestAddress: selectedInterest.Address || '',
      interestCity: selectedInterest.City || '',
      interestState: selectedInterest.State || '',
      interestZip: selectedInterest.Zip || '',
      // Page 4 — More questions
      gq2: [
        { code: 'AAB', answer: '', exp: '' },
        { code: 'AAC', answer: '', exp: '' },
        { code: 'AAD', answer: '', exp: '' },
        { code: 'AAE', answer: '', exp: '' },
        { code: 'AAF', answer: '', exp: '' },
        { code: 'AAG_c', answer: '', exp: '' },
        { code: 'AAG', answer: '', exp: '' },
      ],
      wcCarriers: ['', '', '', ''],
      wcCodes: ['', '', '', ''],
      remarksP4: '',
    });
    if (onDirtyChange) onDirtyChange(false);
  }, [policy?.id]);

  // Load question data from FIG_GeneralQuestions
  useEffect(() => {
    if (!getToken || !insured?.id) return;
    (async () => {
      try {
        const token = await getToken();
        const allGQ = await getItems(token, FIG_LISTS.GENERAL_QUESTIONS);
        const myGQ = allGQ.filter(q => String(q.InsuredId) === String(insured.id));

        // Group by FormType, keyed by QuestionNumber — include record id for updates
        const byType = {};
        for (const q of myGQ) {
          const ft = q.FormType || '';
          if (!byType[ft]) byType[ft] = {};
          byType[ft][q.QuestionNumber] = { id: q.id, answer: q.Answer || '', exp: q.Explanation || '' };
        }

        const contractors = { ...(byType['GL-Contractors'] || {}), ...(byType['126-Contractor'] || {}) };
        const products = { ...(byType['GL-Products'] || {}), ...(byType['126-Products'] || {}) };
        const general = { ...(byType['GL-General'] || {}), ...(byType['126-General'] || {}) };

        setForm(prev => {
          // GL-Contractors Q1-6 → cq[0-5]
          const cq = (prev.cq || []).map((slot, i) => {
            const q = contractors[String(i + 1)];
            return q ? { ...slot, id: q.id, answer: q.answer, exp: q.exp } : slot;
          });

          // GL-Products Q1-10 → pq[0-9]
          const pq = (prev.pq || []).map((slot, i) => {
            const q = products[String(i + 1)];
            return q ? { ...slot, id: q.id, answer: q.answer, exp: q.exp } : slot;
          });

          // GL-General Q1-15 → gq[0-14] (Page 3)
          const gq = (prev.gq || []).map((slot, i) => {
            const q = general[String(i + 1)];
            return q ? { ...slot, id: q.id, answer: q.answer, exp: q.exp } : slot;
          });

          // GL-General Q16-22 → gq2[0-6] (Page 4)
          const gq2 = (prev.gq2 || []).map((slot, i) => {
            const q = general[String(i + 16)];
            return q ? { ...slot, id: q.id, answer: q.answer, exp: q.exp } : slot;
          });

          return { ...prev, cq, pq, gq, gq2 };
        });

        console.log(`[ACORD 126] Loaded questions — Contractors: ${Object.keys(contractors).length}, Products: ${Object.keys(products).length}, General: ${Object.keys(general).length}`);
      } catch (err) {
        console.error('[ACORD 126] Failed to load questions:', err.message);
      }
    })();
  }, [insured?.id]);

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
    if (onDirtyChange) onDirtyChange(true);
  }

  function setHaz(idx, key, val) {
    setForm(prev => {
      const haz = [...prev.haz];
      haz[idx] = { ...haz[idx], [key]: val };
      return { ...prev, haz };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  function setProduct(idx, key, val) {
    setForm(prev => {
      const products = [...prev.products];
      products[idx] = { ...products[idx], [key]: val };
      return { ...prev, products };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  function setQ(arr, idx, key, val) {
    setForm(prev => {
      const qs = [...prev[arr]];
      qs[idx] = { ...qs[idx], [key]: val };
      return { ...prev, [arr]: qs };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  async function handleSave() {
    if (!p.id || !getToken) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const token = await getToken();

      // 1. Policy fields
      await updateItem(token, FIG_LISTS.POLICIES, p.id, {
        CoverageForm: form.coverageForm,
        GeneralAggregateLimit: form.generalAggregate,
        AggregateAppliesPer: form.aggregateAppliesPer,
        ProductsCompOpsLimit: form.productsCompOps,
        PersonalAdvInjuryLimit: form.personalAdvInjury,
        EachOccurrenceLimit: form.eachOccurrence,
        FireDamageLimit: form.fireDamage,
        MedExpenseLimit: form.medExpense,
        EmployeeBenefitsLimit: form.employeeBenefits,
        PDDeductible: form.pdDeductible,
        BIDeductible: form.biDeductible,
        DeductibleBasis: form.deductibleBasis,
        PremOpsPremium: form.premOpsPremium,
        ProductsPremium: form.productsPremium,
        Premium: form.totalPremium ? Number(form.totalPremium) : null,
      });

      // 2. General Questions — cq, pq, gq, gq2
      const insuredId = String(insured.id);
      const saveQuestion = async (slot, formType, questionNumber) => {
        if (slot.id) {
          return updateItem(token, FIG_LISTS.GENERAL_QUESTIONS, slot.id, {
            Answer: slot.answer || '', Explanation: slot.exp || '',
          });
        }
        if (slot.answer || slot.exp) {
          return createItem(token, FIG_LISTS.GENERAL_QUESTIONS, {
            Title: `${formType}-Q${questionNumber}`,
            InsuredId: insuredId,
            FormType: formType,
            QuestionNumber: String(questionNumber),
            Answer: slot.answer || '',
            Explanation: slot.exp || '',
          });
        }
      };

      const qSaves = [];
      (form.cq || []).forEach((slot, i) => qSaves.push(saveQuestion(slot, 'GL-Contractors', i + 1)));
      (form.pq || []).forEach((slot, i) => qSaves.push(saveQuestion(slot, 'GL-Products', i + 1)));
      (form.gq || []).forEach((slot, i) => qSaves.push(saveQuestion(slot, 'GL-General', i + 1)));
      (form.gq2 || []).forEach((slot, i) => qSaves.push(saveQuestion(slot, 'GL-General', i + 16)));
      await Promise.all(qSaves.filter(Boolean));

      // 3. Additional Interest
      if (selectedInterest.id) {
        await updateItem(token, FIG_LISTS.ADDITIONAL_INTERESTS, selectedInterest.id, {
          InterestType: form.interestType,
          EntityName: form.interestName,
          Address: form.interestAddress,
          City: form.interestCity,
          State: form.interestState,
          Zip: form.interestZip,
        });
      } else if (form.interestName) {
        await createItem(token, FIG_LISTS.ADDITIONAL_INTERESTS, {
          Title: form.interestName,
          InsuredId: insuredId,
          InterestType: form.interestType || 'Additional Insured',
          EntityName: form.interestName,
          Address: form.interestAddress,
          City: form.interestCity,
          State: form.interestState,
          Zip: form.interestZip,
        });
      }

      setSaveMsg('Saved');
      if (onDirtyChange) onDirtyChange(false);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  // --- Page nav ---
  const totalPages = 4;
  const navBtnStyle = (disabled) => ({
    width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.1rem', fontWeight: 700, background: disabled ? '#ccc' : '#7eb8da',
    color: '#fff', border: 'none', borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1, fontFamily: 'inherit',
  });
  const pageNav = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center', margin: '0.75rem 0' }}>
      <button style={navBtnStyle(currentPage === 1)} disabled={currentPage === 1}
        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>&lsaquo;</button>
      <span style={{ fontSize: '0.8rem', color: '#666' }}>Page {currentPage} of {totalPages}</span>
      <button style={navBtnStyle(currentPage === totalPages)} disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>&rsaquo;</button>
    </div>
  );

  // --- Render helpers ---
  function renderYNQ(arr, idx, label) {
    const q = form[arr]?.[idx] || {};
    return (
      <div style={{ borderBottom: '1px solid #ddd', padding: '0.3rem 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '0.4rem', alignItems: 'center' }}>
          <span className="acord-label" style={{ whiteSpace: 'normal' }}>{label}</span>
          <div className="acord-checkbox-row" style={{ gap: '0.4rem' }}>
            {YN.map(v => (
              <label className="acord-checkbox" key={v}>
                <input type="radio" name={`${arr}_${idx}`} checked={q.answer === v} onChange={() => setQ(arr, idx, 'answer', v)} />
                <span>{v}</span>
              </label>
            ))}
          </div>
        </div>
        {q.answer === 'Y' && (
          <input className="acord-input" placeholder="Explain..." value={q.exp}
            onChange={e => setQ(arr, idx, 'exp', e.target.value)} style={{ marginTop: '0.25rem' }} />
        )}
      </div>
    );
  }

  function renderLimitRow(label, key) {
    return (
      <div className="acord-row">
        <div className="acord-cell"><span className="acord-label">{label}</span></div>
        <label className="acord-cell acord-cell--sm">
          <input className="acord-input" type="number" value={form[key] || ''} onChange={e => set(key, e.target.value)} />
        </label>
      </div>
    );
  }

  return (
    <div className="acord-form">
      {/* HEADER */}
      <div className="acord-header">
        <div className="acord-header-left">
          <span className="acord-logo">ACORD</span>
          <span className="acord-form-title">ACORD 126 &mdash; COMMERCIAL GENERAL LIABILITY SECTION</span>
        </div>
        <div className="acord-header-right">
          <span style={{ fontSize: '0.8rem', color: '#ccc' }}>Page {currentPage} of {totalPages}</span>
        </div>
      </div>

      {/* PAGE 1 */}
      {currentPage === 1 && <>
        {/* Status of Transaction — UI-only; drives whether CARRIER/NAIC/POLICY # print */}
        <div className="acord-section">
          <div className="acord-section-bar">STATUS OF TRANSACTION</div>
          <div className="acord-section-body">
            <div className="acord-checkbox-row">
              {ACORD_TRANSACTION_STATUSES.map(s => (
                <label className="acord-checkbox" key={s}>
                  <input type="radio" name="transactionStatus126"
                    checked={transactionStatus === s}
                    onChange={() => handleStatusChange(s)} />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Policy Header — read only */}
        <div className="acord-section">
          <div className="acord-section-bar">POLICY</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">PRODUCER</span><input className="acord-input" value="Fragos Insurance Group LLC" readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">POLICY #</span><input className="acord-input" value={form.policyNumber} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">CARRIER</span><input className="acord-input" value={form.carrier} readOnly /></div>
              <div className="acord-cell acord-cell--xs"><span className="acord-label">NAIC</span><input className="acord-input" value={form.naicCode} readOnly /></div>
              <div className="acord-cell"><span className="acord-label">EFF DATE</span><input className="acord-input" value={form.effectiveDate} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell acord-cell--2"><span className="acord-label">NAMED INSURED</span><input className="acord-input" value={insured?.CompanyName || ''} readOnly /></div>
            </div>
          </div>
        </div>

        {/* Coverage Type */}
        <div className="acord-section">
          <div className="acord-section-bar">COVERAGE</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell">
                <span className="acord-label">COVERAGE FORM</span>
                <div className="acord-checkbox-row">
                  {['Occurrence', 'Claims Made', 'O&CP'].map(v => (
                    <label className="acord-checkbox" key={v}>
                      <input type="radio" name="coverageForm" checked={form.coverageForm === v} onChange={() => set('coverageForm', v)} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="acord-cell acord-cell--2">
                <span className="acord-label">OTHER COVERAGE</span>
                <input className="acord-input" value={form.otherCoverage} onChange={e => set('otherCoverage', e.target.value)} />
              </label>
            </div>
          </div>
        </div>

        {/* Limits */}
        <div className="acord-section">
          <div className="acord-section-bar">LIMITS</div>
          <div className="acord-section-body">
            {renderLimitRow('EACH OCCURRENCE', 'eachOccurrence')}
            {renderLimitRow('FIRE DAMAGE (Any One Fire)', 'fireDamage')}
            {renderLimitRow('MEDICAL EXPENSE (Any One Person)', 'medExpense')}
            {renderLimitRow('PERSONAL & ADVERTISING INJURY', 'personalAdvInjury')}
            <div className="acord-row">
              <div className="acord-cell">
                <span className="acord-label">GENERAL AGGREGATE</span>
                <div className="acord-checkbox-row" style={{ marginTop: '0.15rem' }}>
                  <span style={{ fontSize: '0.65rem', color: '#888', marginRight: '0.25rem' }}>Applies per:</span>
                  {['Policy', 'Project', 'Location'].map(v => (
                    <label className="acord-checkbox" key={v}>
                      <input type="radio" name="aggPer" checked={form.aggregateAppliesPer === v} onChange={() => set('aggregateAppliesPer', v)} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="acord-cell acord-cell--sm">
                <input className="acord-input" type="number" value={form.generalAggregate || ''} onChange={e => set('generalAggregate', e.target.value)} />
              </label>
            </div>
            {renderLimitRow('PRODUCTS / COMPLETED OPS AGGREGATE', 'productsCompOps')}
            {renderLimitRow('EMPLOYEE BENEFITS', 'employeeBenefits')}
          </div>
        </div>

        {/* Deductibles */}
        <div className="acord-section">
          <div className="acord-section-bar">DEDUCTIBLES</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">PROPERTY DAMAGE $</span>
                <input className="acord-input" type="number" value={form.pdDeductible} onChange={e => set('pdDeductible', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">BODILY INJURY $</span>
                <input className="acord-input" type="number" value={form.biDeductible} onChange={e => set('biDeductible', e.target.value)} /></label>
              <div className="acord-cell acord-cell--sm">
                <div className="acord-checkbox-row">
                  {['Per Claim', 'Per Occurrence'].map(v => (
                    <label className="acord-checkbox" key={v}>
                      <input type="radio" name="dedBasis" checked={form.deductibleBasis === v} onChange={() => set('deductibleBasis', v)} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Premiums */}
        <div className="acord-section">
          <div className="acord-section-bar">PREMIUMS</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">PREMISES/OPERATIONS</span>
                <input className="acord-input" type="number" value={form.premOpsPremium} onChange={e => set('premOpsPremium', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">PRODUCTS</span>
                <input className="acord-input" type="number" value={form.productsPremium} onChange={e => set('productsPremium', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">OTHER</span>
                <input className="acord-input" type="number" value={form.otherPremium} onChange={e => set('otherPremium', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">TOTAL</span>
                <input className="acord-input" type="number" value={form.totalPremium} onChange={e => set('totalPremium', e.target.value)}
                  style={{ fontWeight: 700 }} /></label>
            </div>
          </div>
        </div>

        {/* Schedule of Hazards */}
        <div className="acord-section">
          <div className="acord-section-bar">SCHEDULE OF HAZARDS</div>
          <div className="acord-section-body" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#eef2f7' }}>
                  {['LOC', 'HAZ', 'CODE', 'CLASSIFICATION', 'BASIS', 'EXPOSURE', 'TERR', 'P/O RATE', 'PROD RATE', 'P/O PREM', 'PROD PREM'].map(h => (
                    <th key={h} style={{ padding: '3px 4px', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.haz?.map((h, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.loc} onChange={e => setHaz(i, 'loc', e.target.value)} style={{ width: '2.5rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.haz} onChange={e => setHaz(i, 'haz', e.target.value)} style={{ width: '2.5rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.classCode} onChange={e => setHaz(i, 'classCode', e.target.value)} style={{ width: '4rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.classification} onChange={e => setHaz(i, 'classification', e.target.value)} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.premBasis} onChange={e => setHaz(i, 'premBasis', e.target.value)} style={{ width: '3rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.exposure} onChange={e => setHaz(i, 'exposure', e.target.value)} style={{ width: '5rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.territory} onChange={e => setHaz(i, 'territory', e.target.value)} style={{ width: '2.5rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.poRate} onChange={e => setHaz(i, 'poRate', e.target.value)} style={{ width: '3.5rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.prodRate} onChange={e => setHaz(i, 'prodRate', e.target.value)} style={{ width: '3.5rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.poPremium} onChange={e => setHaz(i, 'poPremium', e.target.value)} style={{ width: '4rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={h.prodPremium} onChange={e => setHaz(i, 'prodPremium', e.target.value)} style={{ width: '4rem' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Claims Made */}
        <div className="acord-section">
          <div className="acord-section-bar">CLAIMS MADE</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">PROPOSED RETROACTIVE DATE</span>
                <input className="acord-input" value={form.retroDate} onChange={e => set('retroDate', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">ENTRY DATE</span>
                <input className="acord-input" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} /></label>
            </div>
            {renderYNQ('cq', -1, '' /* placeholder — these are on page 1 but use q_AAH/q_AAI directly */)}
          </div>
        </div>

        {/* Employee Benefits */}
        <div className="acord-section">
          <div className="acord-section-bar">EMPLOYEE BENEFITS</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">PER CLAIM DEDUCTIBLE</span>
                <input className="acord-input" type="number" value={form.ebDeductible} onChange={e => set('ebDeductible', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label"># EMPLOYEES</span>
                <input className="acord-input" type="number" value={form.ebEmployees} onChange={e => set('ebEmployees', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label"># COVERED</span>
                <input className="acord-input" type="number" value={form.ebCovered} onChange={e => set('ebCovered', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">RETRO DATE</span>
                <input className="acord-input" value={form.ebRetroDate} onChange={e => set('ebRetroDate', e.target.value)} /></label>
            </div>
          </div>
        </div>
        {pageNav}
      </>}

      {/* PAGE 2 */}
      {currentPage === 2 && <>
        <div className="acord-section">
          <div className="acord-section-bar">CONTRACTORS</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">FULL TIME EMPLOYEES</span>
                <input className="acord-input" type="number" value={form.contractorsFT} onChange={e => set('contractorsFT', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">PART TIME</span>
                <input className="acord-input" type="number" value={form.contractorsPT} onChange={e => set('contractorsPT', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">% SUBCONTRACTED</span>
                <input className="acord-input" type="number" value={form.subcontractedPct} onChange={e => set('subcontractedPct', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">$ PAID TO SUBS</span>
                <input className="acord-input" type="number" value={form.subcontractorsPaid} onChange={e => set('subcontractorsPaid', e.target.value)} /></label>
            </div>
            <div className="acord-row">
              <label className="acord-cell acord-cell--2"><span className="acord-label">TYPE OF WORK SUBCONTRACTED</span>
                <input className="acord-input" value={form.workSubcontracted} onChange={e => set('workSubcontracted', e.target.value)} /></label>
            </div>
            {form.cq?.map((q, i) => renderYNQ('cq', i,
              ['Draw plans for others?', 'Operations include blasting?', 'Operations include excavation?',
               'Subcontractors carry lower limits?', 'Subcontractors work without certificates?', 'Lease equipment to others?'][i] || `Q${q.code}`
            ))}
          </div>
        </div>

        <div className="acord-section">
          <div className="acord-section-bar">PRODUCTS / COMPLETED OPERATIONS</div>
          <div className="acord-section-body">
            {form.products?.map((prod, i) => (
              <div key={i} style={{ borderBottom: '2px solid #bbb', paddingBottom: '0.4rem', marginBottom: '0.4rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#4B0082', padding: '0.2rem 0' }}>Product {i + 1}</div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--2"><span className="acord-label">PRODUCT NAME</span>
                    <input className="acord-input" value={prod.name} onChange={e => setProduct(i, 'name', e.target.value)} /></label>
                  <label className="acord-cell"><span className="acord-label">ANNUAL GROSS SALES</span>
                    <input className="acord-input" type="number" value={prod.grossSales} onChange={e => setProduct(i, 'grossSales', e.target.value)} /></label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--sm"><span className="acord-label">UNITS</span>
                    <input className="acord-input" value={prod.units} onChange={e => setProduct(i, 'units', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--sm"><span className="acord-label">MONTHS IN MARKET</span>
                    <input className="acord-input" value={prod.monthsInMarket} onChange={e => setProduct(i, 'monthsInMarket', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--sm"><span className="acord-label">EXPECTED LIFE</span>
                    <input className="acord-input" value={prod.expectedLife} onChange={e => setProduct(i, 'expectedLife', e.target.value)} /></label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell"><span className="acord-label">INTENDED USE</span>
                    <input className="acord-input" value={prod.intendedUse} onChange={e => setProduct(i, 'intendedUse', e.target.value)} /></label>
                  <label className="acord-cell"><span className="acord-label">PRINCIPAL COMPONENTS</span>
                    <input className="acord-input" value={prod.components} onChange={e => setProduct(i, 'components', e.target.value)} /></label>
                </div>
              </div>
            ))}
            {form.pq?.map((q, i) => renderYNQ('pq', i,
              ['Install or service products?', 'Components sourced from others?', 'R&D conducted / new products planned?',
               'Guarantees, warranties, or hold harmless?', 'Products related to aircraft industry?',
               'Products recalled or discontinued?', 'Products of others sold under your label?',
               'Your products under label of others?', 'Vendor coverage required?', 'Named insured sell to other named insured?'][i] || `Q${q.code}`
            ))}
          </div>
        </div>
        {pageNav}
      </>}

      {/* PAGE 3 */}
      {currentPage === 3 && <>
        <div className="acord-section">
          <div className="acord-section-bar">GENERAL INFORMATION</div>
          <div className="acord-section-body">
            {form.gq?.map((q, i) => renderYNQ('gq', i,
              ['Medical facilities provided / medical professionals employed?',
               'Any exposure to radioactive materials?',
               'Operations involve storing/disposing/transporting hazardous material?',
               'Any listed operations sold or acquired in last 5 years?',
               'Machinery or equipment loaned/rented to others?',
               'Watercraft, docks, or floats owned/hired/leased?',
               'Any parking facilities owned or rented?',
               'Fee charged for parking?',
               'Recreational facilities provided?',
               'Lodging / apartment operations?',
               'Swimming pool?',
               'Sporting or social events sponsored?',
               'Athletic teams?',
               'Structural alterations contemplated?',
               'Demolition exposure contemplated?'][i] || `Q${q.code}`
            ))}
          </div>
        </div>

        {/* Additional Interest */}
        <div className="acord-section">
          <div className="acord-section-bar">ADDITIONAL INTEREST</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell acord-cell--2"><span className="acord-label">NAME</span>
                <input className="acord-input" value={form.interestName} onChange={e => set('interestName', e.target.value)} /></label>
            </div>
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">ADDRESS</span>
                <input className="acord-input" value={form.interestAddress} onChange={e => set('interestAddress', e.target.value)} /></label>
              <label className="acord-cell acord-cell--sm"><span className="acord-label">CITY</span>
                <input className="acord-input" value={form.interestCity} onChange={e => set('interestCity', e.target.value)} /></label>
              <label className="acord-cell acord-cell--xs"><span className="acord-label">STATE</span>
                <input className="acord-input" value={form.interestState} onChange={e => set('interestState', e.target.value)} maxLength={2} /></label>
              <label className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
                <input className="acord-input" value={form.interestZip} onChange={e => set('interestZip', e.target.value)} /></label>
            </div>
          </div>
        </div>
        {pageNav}
      </>}

      {/* PAGE 4 */}
      {currentPage === 4 && <>
        <div className="acord-section">
          <div className="acord-section-bar">GENERAL INFORMATION (Cont'd)</div>
          <div className="acord-section-body">
            {form.gq2?.map((q, i) => renderYNQ('gq2', i,
              ['Applicant active in any joint ventures?',
               'Any subsidiaries or newly acquired organizations?',
               'Labor interchange with other businesses/subsidiaries?',
               'Day care facilities operated?',
               'Any crimes occurred or attempted in last 3 years?',
               'Formal safety/security policy in effect?',
               'Business promotional literature contain safety representations?'][i] || `Q${q.code}`
            ))}
          </div>
        </div>

        {/* Remarks */}
        <div className="acord-section">
          <div className="acord-section-bar">REMARKS</div>
          <div className="acord-section-body">
            <textarea className="acord-input" rows={4} value={form.remarksP4}
              onChange={e => set('remarksP4', e.target.value)}
              style={{ resize: 'vertical', minHeight: '3rem', padding: '0.4rem' }}
              placeholder="Additional remarks..." />
          </div>
        </div>

        {/* Signature */}
        <div className="acord-section">
          <div className="acord-section-bar">SIGNATURE</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">PRODUCER SIGNATURE</span>
                <input className="acord-input" value="Marc Fragos" readOnly style={{ fontStyle: 'italic' }} /></div>
              <div className="acord-cell"><span className="acord-label">DATE</span>
                <input className="acord-input" value={form.date} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">APPLICANT SIGNATURE</span>
                <input className="acord-input" value={insured?.CompanyName || ''} readOnly style={{ fontStyle: 'italic' }} /></div>
              <div className="acord-cell"><span className="acord-label">DATE</span>
                <input className="acord-input" value={form.date} readOnly /></div>
            </div>
          </div>
        </div>
        {pageNav}
      </>}

      {/* Actions */}
      <div className="acord-actions">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; Back to ACORDs</button>
        <button className="FIG-save-btn" onClick={handleSave} disabled={saving || !p.id}>
          {saving ? 'Saving...' : 'Save Coverage'}
        </button>
        {saveMsg && <span className={'FIG-save-msg' + (saveMsg.startsWith('Error') ? ' FIG-save-msg--error' : '')}>{saveMsg}</span>}
        <button className="FIG-save-btn acord-pdf-btn" disabled={generating} onClick={async () => {
          setGenerating(true);
          try {
            await generateACORD126(form, insured, getToken, transactionStatus);
          } catch (err) {
            console.error('[ACORD 126] PDF generation failed:', err);
            alert('Error generating PDF: ' + err.message);
          } finally {
            setGenerating(false);
          }
        }}>
          {generating ? 'Generating...' : 'Generate PDF'}
        </button>
      </div>
    </div>
  );
}

export default ACORD126;
